import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import type { ButtonProps } from '@mui/material';
import { Button } from '@mui/material';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';

import useFollowUpFilters from '../hooks/useFollowUpFilters';
import { useTicketTableContext } from '../TicketFilterContext';

const TicketsDownloadButton: FC<ButtonProps> = (props) => {
  const { t } = useTranslation(['followUp', 'downloadManager']);

  const [downloadStarted, setDownloadStarted] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const { poll } = useDownloadManager();
  const { followUpFilters, dateRange, orgUnit } = useFollowUpFilters();
  const { debouncedSearchString, sortBy } = useTicketTableContext();

  const onTicketsDownload = useCallback(() => {
    // Prevent accidental double-clicks.
    setIsDisabled(true);
    HttpServiceInstance.post(
      apiPaths.FOLLOW_UP.TICKETS.EXPORT,
      {},
      // Same parameters as the ticket list table minus pagination.
      {
        search: debouncedSearchString,
        ordering: sortBy,
        orgUnit,
        ...dateRange,
        ...followUpFilters,
      },
    ).finally(() => {
      poll();
      setDownloadStarted(true);
    });
  }, [debouncedSearchString, sortBy, orgUnit, dateRange, followUpFilters]);

  useEffect(() => {
    // Reset double-click prevention.
    if (isDisabled) {
      const timer = setTimeout(() => {
        setIsDisabled(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isDisabled]);

  return (
    <>
      <Button
        variant="cta"
        color="primary"
        startIcon={<DownloadRounded />}
        onClick={onTicketsDownload}
        disabled={isDisabled}
        {...props}
      >
        {t('followUp:downloadTickets')}
      </Button>

      <SuccessSnackbar
        open={downloadStarted}
        onClose={() => setDownloadStarted(false)}
        title={t('downloadManager:DownloadManager')}
        message={t('followUp:downloadMessage')}
      />
    </>
  );
};

export default TicketsDownloadButton;
